import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/authStore';
import { 
  FolderKanban, 
  FileText, 
  MessageSquare, 
  Clock, 
  CheckCircle2, 
  ArrowRight, 
  Download,
  AlertCircle,
  Sparkles,
  ChevronRight,
  BookOpen
} from 'lucide-react';
import { PUBLISHING_STAGES_CONFIG } from '@ftt/shared';

export const ClientDashboardPage: React.FC = () => {
  const { user, clientProfile } = useAuth();

  const { data: dashboardData, isLoading } = useQuery({
    queryKey: ['client-dashboard'],
    queryFn: () => api.get('/client/dashboard').then((res) => res.data.data),
  });

  if (isLoading) {
    return <div className="p-8 text-center text-xs font-mono text-slate-500">Loading client dashboard...</div>;
  }

  const activeProjects = dashboardData?.activeProjects || [];
  const recentDocuments = dashboardData?.recentDocuments || [];
  const recentThreads = dashboardData?.recentThreads || [];
  const notifications = dashboardData?.notifications || [];

  return (
    <div className="space-y-8">
      
      {/* Welcome Banner */}
      <div className="p-6 sm:p-8 rounded-2xl bg-gradient-to-r from-editorial-dark to-slate-900 text-white shadow-md flex flex-col md:flex-row md:items-center justify-between gap-6 border border-slate-800">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded bg-brand-900/60 border border-brand-500/40 text-brand-300 text-[10px] font-mono uppercase tracking-wider font-bold">
            Author Workspace
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-white">
            Welcome, {user?.firstName} {user?.lastName}
          </h1>
          <p className="text-xs sm:text-sm text-slate-300 max-w-xl font-light">
            {clientProfile?.companyName || user?.organization || 'For The Truth Publishing Author'} &bull; Track milestone progress, download proofs, and collaborate with your assigned editor.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/submit-enquiry"
            className="px-4 py-2.5 rounded-lg bg-brand-500 hover:bg-brand-600 text-slate-950 font-bold text-xs shadow-sm transition-colors"
          >
            + Submit New Project
          </Link>
          <Link
            to="/client/documents"
            className="px-4 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white font-semibold text-xs border border-slate-700 transition-colors"
          >
            Document Centre
          </Link>
        </div>
      </div>

      {/* Active Projects Overview */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-xl font-bold text-slate-900 flex items-center gap-2">
            <FolderKanban className="w-5 h-5 text-brand-700" />
            <span>Active Publishing Projects</span>
          </h2>
          <Link to="/client/projects" className="text-xs font-bold text-brand-700 hover:underline">
            View All Projects &rarr;
          </Link>
        </div>

        {activeProjects.length === 0 ? (
          <div className="p-8 rounded-2xl bg-white border border-editorial-border text-center space-y-3">
            <BookOpen className="w-10 h-10 text-slate-300 mx-auto" />
            <h3 className="font-serif font-bold text-slate-700 text-sm">No Active Publishing Projects Yet</h3>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              If you have recently submitted a manuscript proposal, our acquisitions team is reviewing it. You will receive an update shortly.
            </p>
            <Link
              to="/submit-enquiry"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-600 text-white text-xs font-bold"
            >
              <span>Submit Manuscript Enquiry</span>
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {activeProjects.map((proj: any) => {
              const currentStageConfig = PUBLISHING_STAGES_CONFIG.find((s) => s.status === proj.status);
              const completedCount = proj.milestones?.filter((m: any) => m.status === 'COMPLETED').length || 0;
              const totalMilestones = proj.milestones?.length || 12;
              const progressPct = Math.round((completedCount / totalMilestones) * 100);

              return (
                <div
                  key={proj.id}
                  className="p-6 sm:p-8 rounded-2xl bg-white border border-editorial-border shadow-xs space-y-6"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-bold uppercase">
                          {proj.projectCode}
                        </span>
                        <span className="text-xs text-brand-700 font-bold font-mono">
                          {proj.category}
                        </span>
                      </div>
                      <h3 className="font-serif text-2xl font-bold text-slate-900">
                        {proj.title}
                      </h3>
                      <p className="text-xs text-slate-500">
                        Author: <strong>{proj.author}</strong> &bull; Started: {proj.startDate ? new Date(proj.startDate).toLocaleDateString() : 'Active'}
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="text-right sm:block hidden">
                        <span className="text-[10px] text-slate-400 font-mono uppercase block">Current Status</span>
                        <span className="text-xs font-bold text-brand-800 bg-brand-50 px-2.5 py-1 rounded border border-brand-200 inline-block mt-0.5">
                          {currentStageConfig?.label || proj.status}
                        </span>
                      </div>
                      <Link
                        to={`/client/projects/${proj.id}`}
                        className="px-4 py-2 rounded-lg bg-editorial-dark text-white text-xs font-bold hover:bg-slate-800 transition-colors"
                      >
                        Project Details &rarr;
                      </Link>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs font-mono">
                      <span className="text-slate-600 font-medium">
                        Publishing Milestone Progression ({completedCount} of {totalMilestones} Completed)
                      </span>
                      <span className="font-bold text-brand-800">{progressPct}%</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                      <div
                        className="bg-brand-600 h-2.5 rounded-full transition-all duration-700"
                        style={{ width: `${progressPct}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Next Milestone Note */}
                  {proj.nextMilestone && (
                    <div className="p-4 rounded-xl bg-editorial-parchment border border-brand-200 flex items-start gap-3">
                      <Clock className="w-4 h-4 text-brand-700 shrink-0 mt-0.5" />
                      <div className="text-xs space-y-0.5">
                        <span className="font-bold text-slate-900 block">
                          Next Actionable Milestone: {proj.nextMilestone.title}
                        </span>
                        <p className="text-slate-600">{proj.nextMilestone.description}</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 2-Column Grid: Recent Documents & Communications */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Document Centre Card */}
        <div className="p-6 rounded-2xl bg-white border border-editorial-border shadow-xs space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-lg font-bold text-slate-900 flex items-center gap-2">
              <FileText className="w-4 h-4 text-brand-700" />
              <span>Recent Project Files</span>
            </h3>
            <Link to="/client/documents" className="text-xs font-bold text-brand-700 hover:underline">
              All Files &rarr;
            </Link>
          </div>

          {recentDocuments.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-500">No documents uploaded yet.</div>
          ) : (
            <div className="space-y-2">
              {recentDocuments.map((doc: any) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100 text-xs hover:bg-slate-100/70 transition-colors"
                >
                  <div className="overflow-hidden space-y-0.5 pr-2">
                    <span className="font-bold text-slate-800 block truncate">{doc.title}</span>
                    <span className="text-[10px] text-slate-400 font-mono uppercase">
                      {doc.category} &bull; {Math.round(doc.fileSize / 1024)} KB
                    </span>
                  </div>
                  <a
                    href={`/api/client/documents/${doc.id}/download`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded bg-white border border-slate-200 text-slate-700 hover:text-brand-800 hover:border-brand-300"
                    title="Download File"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Messaging & Communications Card */}
        <div className="p-6 rounded-2xl bg-white border border-editorial-border shadow-xs space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-lg font-bold text-slate-900 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-brand-700" />
              <span>Editorial Communications</span>
            </h3>
            <Link to="/client/messages" className="text-xs font-bold text-brand-700 hover:underline">
              Open Chat &rarr;
            </Link>
          </div>

          {recentThreads.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-500">No message threads active.</div>
          ) : (
            <div className="space-y-2">
              {recentThreads.map((thr: any) => (
                <Link
                  key={thr.id}
                  to="/client/messages"
                  className="block p-3 rounded-lg bg-slate-50 border border-slate-100 hover:bg-slate-100/70 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-xs text-slate-900 truncate">{thr.title}</span>
                    <span className="text-[10px] text-slate-400 font-mono">
                      {new Date(thr.lastMessageAt).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Direct conversation with assigned editorial staff.
                  </p>
                </Link>
              ))}
            </div>
          )}
        </div>

      </div>

    </div>
  );
};
